import { useTranslation } from "react-i18next";

import type { components } from "@/api/types";
import { formatMoney, formatPercent } from "@/shared/lib";
import { Icon } from "@/shared/ui";

import { EarningsSpark } from "./EarningsSpark";

type ChartPoint = components["schemas"]["ChartPoint"];

/**
 * Главная карточка приложения: сумма за период, дельта к прошлому периоду и
 * график под ними.
 *
 * `delta` может не быть вовсе, и это не то же самое, что ноль: сравнивать не с
 * чем, когда предыдущий период пуст или выбран период «всё время». Показать
 * «+0,0%» значило бы соврать — рост с нуля не равен отсутствию роста.
 *
 * Глаз прячет сумму, а не всю карточку: график остаётся, потому что по нему
 * никто через плечо ничего не прочитает.
 */
interface EarningsCardProps {
  eyebrow: string;
  total: string;
  deltaPct: string | null | undefined;
  deltaCaption: string;
  chart: ChartPoint[];
  labels: string[];
  hidden: boolean;
  onToggleHidden: () => void;
}

export function EarningsCard({
  eyebrow,
  total,
  deltaPct,
  deltaCaption,
  chart,
  labels,
  hidden,
  onToggleHidden,
}: EarningsCardProps) {
  const { t } = useTranslation();

  return (
    <section
      className={[
        "relative overflow-hidden rounded-lg border border-status-profit/30 p-3",
        "bg-[radial-gradient(circle_at_80%_50%,color-mix(in_srgb,var(--color-status-profit)_10%,transparent),transparent_28%),linear-gradient(180deg,color-mix(in_srgb,var(--color-status-profit)_6%,var(--palette-bg)),var(--palette-bg))]",
        "shadow-[inset_0_0_32px_color-mix(in_srgb,var(--color-status-profit)_4%,transparent)]",
      ].join(" ")}
    >
      <div className="relative z-1">
        <p className="text-caption text-text-secondary">{eyebrow}</p>

        <p className="mt-1 text-display tracking-tight text-text-bright">
          {hidden ? "••••••" : formatMoney(total, { sign: "always", suffix: "USDT" })}
        </p>

        <p className="mt-1 text-small">
          {deltaPct === null || deltaPct === undefined ? (
            <span className="text-text-secondary">{t("earnings.noDelta")}</span>
          ) : (
            <>
              <span className="font-semibold text-status-profit">
                {formatPercent(deltaPct, { sign: "always" })}
              </span>
              <span className="ml-1.5 text-text-secondary">{deltaCaption}</span>
            </>
          )}
        </p>
      </div>

      <button
        type="button"
        data-haptic="tap"
        aria-pressed={hidden}
        aria-label={t(hidden ? "home.show" : "home.hide")}
        onClick={onToggleHidden}
        className="press absolute top-3 right-3 z-2 text-text-secondary"
      >
        <Icon name={hidden ? "eyeSlash" : "eye"} size={17} />
      </button>

      {/* Радар с тикером — единственный элемент карточки, который ничего не
          сообщает. Он и в макете декоративный: даёт правому краю вес, чтобы
          сумма слева не висела в пустоте. */}
      <span
        aria-hidden="true"
        className={[
          "pointer-events-none absolute top-[30%] right-[11%] flex size-12 items-center justify-center rounded-full",
          "bg-[repeating-radial-gradient(circle,color-mix(in_srgb,var(--color-status-profit)_11%,transparent)_0_1px,transparent_1px_6px)]",
          "text-h3 font-extrabold text-status-profit",
          "[text-shadow:0_0_6px_color-mix(in_srgb,var(--color-status-profit)_35%,transparent)]",
        ].join(" ")}
      >
        T
        <span className="absolute inset-2.5 rounded-full border border-status-profit/20" />
      </span>

      {/* Подписи оси — своей полосой под графиком, а не поверх него, как в
          макете. Там линия всегда идёт по диагонали и подписи её не задевают;
          на реальных данных ряд бывает плоским (робот куплен неделю назад, до
          этого нули) и ложится ровно на подписи, перечёркивая их. */}
      <div className="relative -mx-3 mt-2">
        <div className="h-[94px]">
          <EarningsSpark points={chart} />
        </div>
        {labels.length > 0 ? (
          <div className="flex justify-between px-3 pt-1 text-micro text-text-tertiary">
            {/* Ключ по индексу, а не по подписи: на суточном окне соседние
                отметки могут совпасть («00:00» в начале и в конце), и React
                молча схлопнул бы их в один узел. */}
            {labels.map((label, index) => (
              <span key={index} className="shrink-0 whitespace-nowrap">
                {label}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
